import React from "react";
import { normalizeMentionItem, type MentionSuggestionItem } from "@seavien/commons";
import { Skeleton } from "@/components/ui/skeleton";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import type { Localizer } from "@/main/react/Types";

export type MentionSuggestionTrailingMark = {
    label: string;
    /** Tone classes for the pill, so an embedder can reuse a vocabulary it already renders elsewhere. */
    toneClassName: string;
};

type MentionSuggestionPopupProps = {
    /**
     * `FA-R3`. Supplied by the tiptap factory that owns this menu (`createSuggestionMenuAria`), and
     * ABSENT for the two non-tiptap consumers — `component/UsernameSuggestionField.tsx` and
     * `chat/StartChatSection.tsx`, the second of which renders this popup `embedded` as the SECOND of
     * two lists inside one popover. The listbox/option roles are emitted only when an owner supplies
     * an id, because giving one of two sibling lists a listbox role and not the other is a worse
     * accessibility statement than giving neither. Pinned in both directions by
     * `editor/suggestionMenuAria.contract.spec.tsx`.
     */
    listboxId?: string;
    optionIdFor?: (index: number) => string;
    /** For each row avatar's localized `alt` (round 25 `D8`); `ProfileImage` requires one. */
    localizer: Localizer;
    items: MentionSuggestionItem[];
    selectedIndex: number;
    onHover: (index: number) => void;
    onSelect: (index: number) => void;
    highlightQuery?: string;
    isLoading?: boolean;
    loadingRowCount?: number;
    /**
     * Render as the CONTENT of a surrounding surface instead of as its own floating card: no border,
     * no shadow, no radius and no min-width, so one menu is one bordered, one shadowed surface at one
     * width. The chat start-chat picker embeds this list above its "start with @user" option; the
     * editor's mention popup passes nothing and keeps the floating card.
     */
    embedded?: boolean;
    /**
     * Optional trailing pill per row, resolved from the row's username. The chat picker uses it to say
     * which suggestions are conversations the member already holds — without it, every suggestion in
     * that picker was an existing thread and nothing in the row said so.
     */
    trailingMarkForUsername?: (username: string) => MentionSuggestionTrailingMark | null;
    /**
     * Which name leads the row.
     *
     * <p><b>`"handle"` (the default) is the EDITOR's answer and is right there</b>: the mention popup
     * exists to insert `@handle` into a body, so the string the member is choosing is the string the
     * row must show. Nothing about that consumer changes.
     *
     * <p><b>`"name"` is the chat picker's answer.</b> That picker chooses a PERSON, and the inbox rows
     * 300px below it in the same column identify the same people by display name with no sigil. In the
     * devo fixture handle and display name happen to be the same string so the mismatch is only
     * cosmetic; in production they differ, and then a member reads "@jharper44" in the picker and
     * "Jamie" in the inbox and cannot tell they are the same conversation — which is precisely what the
     * trailing "Your turn" / "Waiting for a reply" mark is trying to tell them.
     *
     * <p>An opt-in prop rather than a rewrite, because the two consumers legitimately differ. Theme T1
     * of the chat register says fix a defect once in the shared place; it does not say make two
     * surfaces the same when they are answering different questions.
     *
     * <p>Register rows: `suggestion-row-identifies-people-differently-from-the-inbox` and
     * `chat-suggestion-secondary-line-repeats-the-inbox-preview`.
     */
    identifyBy?: "handle" | "name";
};

function renderHighlightedText(value: string, highlightQuery?: string) {
    const normalizedQuery = String(highlightQuery ?? "").trim();
    if (!normalizedQuery) {
        return value;
    }

    const normalizedValue = value.toLowerCase();
    const normalizedHighlight = normalizedQuery.toLowerCase();
    const matchIndex = normalizedValue.indexOf(normalizedHighlight);
    if (matchIndex < 0) {
        return value;
    }

    const matchEnd = matchIndex + normalizedHighlight.length;
    return (
        <>
            {value.slice(0, matchIndex)}
            {/*
              * A WASH, not a weight step. This was `font-semibold text-foreground` inside a row already
              * set to `font-medium text-[var(--foreground)]`, so the only difference between the matched
              * characters and the rest of the name was 500 vs 600 at an IDENTICAL colour: at 800% zoom
              * "Test" and "User2" in "@TestUser2" were indistinguishable, and the component was
              * computing a distinction nothing rendered.
              *
              * A match highlight answers "why did this row come back", so it has to be legible at 14px.
              * The fill is what does that; the weight and the ink step stay as reinforcement. Built from
              * `--primary-action` at a low alpha rather than a yellow marker, because that is the
              * token this surface already uses for "the thing you are acting on", and it follows the
              * theme in both directions. Register row: `suggestion-match-highlight-is-imperceptible`.
              */}
            <span className="rounded-sm bg-[var(--primary-action)]/15 px-0.5 font-semibold text-foreground">
                {value.slice(matchIndex, matchEnd)}
            </span>
            {value.slice(matchEnd)}
        </>
    );
}

export default function MentionSuggestionPopup({
    localizer,
    items,
    selectedIndex,
    onHover,
    onSelect,
    highlightQuery,
    isLoading = false,
    loadingRowCount = 3,
    embedded = false,
    trailingMarkForUsername,
    identifyBy = "handle",
    listboxId,
    optionIdFor,
}: MentionSuggestionPopupProps) {
    const optionId = (index: number) => (listboxId && optionIdFor ? optionIdFor(index) : undefined);
    return (
        <div
            aria-busy={isLoading}
            role={listboxId ? "listbox" : undefined}
            id={listboxId}
            className={embedded
                ? "p-1"
                : "overflow-hidden rounded-2xl border border-border bg-popover shadow-[var(--surface-shadow-lg)] min-w-[18rem] max-w-[min(24rem,calc(100vw-1.5rem))] p-1"}
        >
            {isLoading && items.length === 0 ? Array.from({ length: loadingRowCount }, (_, index) => (
                <div
                    key={`mention-suggestion-skeleton-${index}`}
                    data-testid="mention-suggestion-skeleton-row"
                    className="flex items-center gap-3 rounded-xl px-3 py-2"
                >
                    <Skeleton className="size-9 shrink-0 rounded-full bg-[var(--background3)]" />

                    <div className="min-w-0 flex-1 space-y-2">
                        <Skeleton className="h-4 w-24 rounded-full bg-[var(--background3)]" />
                        <Skeleton className="h-3 w-40 max-w-full rounded-full bg-[var(--background3)]/80" />
                    </div>
                </div>
            )) : items.map((rawItem, index) => {
                const item = normalizeMentionItem(rawItem);
                const username = item.username ?? item.id;
                const trailingMark = trailingMarkForUsername?.(username) ?? null;
                /*
                 * `label` is the display name and it has been computed all along — the chat picker's
                 * `createSuggestionItemFromThread` fills it with a comment explaining why — and then
                 * never rendered by anything. Under `identifyBy="name"` it leads the row and the handle
                 * takes the second line, which is the slot the message preview used to occupy.
                 *
                 * The fallback is deliberate and it is what keeps the fixture honest: when there is no
                 * label, or it is the SAME string as the handle (which is true of every seeded devo
                 * account, and of a real member who never set a display name), the row stays exactly as
                 * it is today rather than printing one name twice under itself.
                 */
                const displayName = item.label?.trim() ?? "";
                const leadsWithName = identifyBy === "name"
                    && displayName.length > 0
                    && displayName.toLowerCase() !== username.toLowerCase();
                const description = leadsWithName ? `@${username}` : item.description?.trim();

                return (
                    <button
                        key={`${item.id}-${index}`}
                        type="button"
                        id={optionId(index)}
                        role={listboxId ? "option" : undefined}
                        aria-selected={listboxId ? index === selectedIndex : undefined}
                        aria-label={`@${username}`}
                        data-playwright-id="mention-suggestion-item"
                        // One wash vocabulary, not two raw steps of the background ramp. The selected
                        // row painted `bg-[var(--background3)]` — gray-200, rgb(229,231,235) against a
                        // menu surface of rgb(249,251,252) — so at rest, with no pointer on the screen,
                        // the heaviest element in the menu was the highlight rather than any of the
                        // names it was marking; and because ArrowDown moves the selection, hovering one
                        // row while another is selected showed `--background3` and `--background2` at
                        // once. `bg-accent` (rgb(237,238,241) in light) is the semantic
                        // active-descendant fill and `standard-hover` is the app's single
                        // `--hover-wash`. Register row:
                        // `suggestion-row-washes-are-raw-background-steps`.
                        //
                        // The ring is spelled here because `standard-hover` carries
                        // `:focus-visible { outline: none }` and gives nothing back — the defect class
                        // `flatXRestingSurfaces.contract.test.tsx` exists to catch. This row was
                        // originally written with a note reasoning the ring was unnecessary, since the
                        // row takes `onMouseDown` + `preventDefault` and so never receives focus by
                        // POINTER. That is true of the pointer and false of the keyboard: it is a real
                        // `<button>` with no `tabIndex`, so Tab still reaches it. Added additively, in
                        // the same shape as the other fifteen sites the 2026-08-15 sweep fixed.
                        //
                        // ⚠️ Open, and deliberately NOT decided at integration time: this row is a dead
                        // tab stop either way — there is no `onClick` and no `onKeyDown`, so Tab-then-
                        // Enter does nothing. The active-descendant pattern this popup follows wants
                        // `tabIndex={-1}` on the rows, which would make the original note's claim
                        // structurally true. That is a keyboard-behaviour change and belongs to whoever
                        // owns the mention popup, not to the merge that surfaced it.
                        className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition [@media(pointer:coarse)]:min-h-11 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] ${index === selectedIndex ? "bg-accent" : "standard-hover"}`}
                        onMouseEnter={() => onHover(index)}
                        onMouseDown={(event) => {
                            event.preventDefault();
                            onSelect(index);
                        }}
                    >
                        <ProfileImage localizer={localizer}
                            imageUrl={item.profileImageUrl}
                            username={username}
                            mode="user"
                            hoverable={false}
                            className="size-9"
                        />

                        <div className="min-w-0 flex-1">
                            <div
                                className="truncate text-sm font-medium text-[var(--foreground)]"
                                data-mention-suggestion-identity={leadsWithName ? "name" : "handle"}
                                translate="no"
                            >
                                {leadsWithName
                                    ? renderHighlightedText(displayName, highlightQuery)
                                    : <>@{renderHighlightedText(username, highlightQuery)}</>}
                            </div>

                            {description ? (
                                <div
                                    className="truncate text-xs text-[var(--neutral-text)]"
                                    data-playwright-id="mention-suggestion-secondary"
                                    translate={leadsWithName ? "no" : undefined}
                                >
                                    {leadsWithName
                                        ? renderHighlightedText(description, highlightQuery)
                                        : description}
                                </div>
                            ) : null}
                        </div>

                        {trailingMark ? (
                            <span
                                className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${trailingMark.toneClassName}`}
                                data-playwright-id="mention-suggestion-trailing-mark"
                            >
                                {trailingMark.label}
                            </span>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}
